import { viewActionCreators } from '@opengov/component-library-classic/module/view';
import { IStory, IStoryContent } from '@opengov/stories-shared/types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Close as CloseIcon } from '@opengov/react-capital-assets';
import React from 'react';
import Dropzone, { Accept, FileRejection } from 'react-dropzone';

import { sanitizeUrl } from '@braintree/sanitize-url';
import { getFileSizeInBytes, getS3SignUrl } from '../../../stories/common/utils';
import {
  MAX_IMAGE_UPLOAD_SIZE,
  MAX_VIDEO_UPLOAD_SIZE,
  MEDIA_BACKGROUND,
  MEDIA_IMAGE,
  MEDIA_VIDEO,
  MIME_IMAGE,
  MIME_VIDEO,
  TAG_A,
  TAG_DIV,
  TAG_IMAGE,
  TAG_VIDEO
} from '../../../stories/constants';
import { addBackground, addMedia, getStyles, removeBackground, removeMedia } from '../../../stories/utils';
import { IModalMessages, IUpdateStoryContentProps } from '../../../types';

import styles from './AddMedia.scss';
import { styles as muiStyles } from './AddMedia.styles';

export type IAddMediaProps = {
  isTemplate: boolean;
  story: IStory;
  entitySubdomain: string;
  mediaType: 'image' | 'video' | 'background';
  storyContent: IStoryContent;
  messages: IModalMessages;
  onClose(): void;
  updateStoryContent(storyContent: IStoryContent): void;
  updateStoryContentDB(props: IUpdateStoryContentProps): void;
};

type IAddMediaState = {
  isUpdating: boolean;
  isUploading: boolean;
  url: string;
  height: string;
  width: string;
  alt: string;
  transcriptLink: string;
  imageLink: string;
  file: any;
  percent: number;
  message: string;
};

const ACCEPT_IMAGE: Accept = {
  [MIME_IMAGE]: []
};

const ACCEPT_VIDEO: Accept = {
  [MIME_VIDEO]: ['.mp4']
};

export class AddMedia extends React.Component<IAddMediaProps, IAddMediaState> {
  constructor(props) {
    super(props);

    const {
      mediaType = MEDIA_IMAGE,
      storyContent: { content, mediaIndex = 0 }
    } = props;
    let url = '';
    let alt = '';
    let transcriptLink = '';
    let imageLink = '';
    let height = null;
    let width = null;

    if (content.indexOf('media-block') > -1) {
      const fragment = document.createRange().createContextualFragment(content);
      const mediaBlocks = fragment.querySelectorAll('.media-block');
      const mediaBlock = mediaBlocks[mediaIndex];
      for (let i = 0; i < mediaBlock.children.length; i++) {
        if (mediaBlock.children[i].tagName === TAG_IMAGE || mediaBlock.children[i].tagName === TAG_VIDEO) {
          if (mediaBlock.children[i].hasAttribute('src')) {
            url = mediaBlock.children[i].getAttribute('src');
          }
          if (mediaBlock.children[i].hasAttribute('height')) {
            height = mediaBlock.children[i].getAttribute('height');
          }
          if (mediaBlock.children[i].hasAttribute('width')) {
            width = mediaBlock.children[i].getAttribute('width');
          }
          if (mediaBlock.children[i].hasAttribute('alt')) {
            alt = mediaBlock.children[i].getAttribute('alt');
          }
        } else if (
          mediaBlock.children[i].tagName === TAG_DIV &&
          mediaBlock.children[i].classList &&
          mediaBlock.children[i].classList.contains('transcript-link')
        ) {
          const firstChild: Partial<HTMLElement> = mediaBlock.children[i].firstChild;
          if (firstChild && firstChild.tagName === TAG_A && firstChild.hasAttribute('href')) {
            transcriptLink = firstChild.getAttribute('href');
          }
        } else if (mediaBlock.children[i].tagName === TAG_A) {
          const firstChild: Partial<HTMLElement> = mediaBlock.children[i].firstChild;
          if (mediaBlock.children[i].hasAttribute('href')) {
            imageLink = mediaBlock.children[i].getAttribute('href');
          }
          if (firstChild && firstChild.tagName === TAG_IMAGE) {
            if (firstChild.hasAttribute('src')) {
              url = firstChild.getAttribute('src');
            }
            if (mediaBlock.children[i].hasAttribute('height')) {
              height = firstChild.getAttribute('height');
            }
            if (firstChild.hasAttribute('width')) {
              width = firstChild.getAttribute('width');
            }
            if (firstChild.hasAttribute('alt')) {
              alt = firstChild.getAttribute('alt');
            }
          }
        }
      }
    } else if (
      mediaType === MEDIA_BACKGROUND &&
      content.indexOf('<header') > -1 &&
      content.indexOf('background-image:') > -1
    ) {
      const fragment = document.createRange().createContextualFragment(content);
      const header = fragment.querySelectorAll('header')[0];
      if (header && header.hasAttribute('style')) {
        const styleObj = getStyles(header.getAttribute('style'));
        if (styleObj.backgroundImage) {
          url = styleObj.backgroundImage.slice(4, -1).replace(/"/g, '');
        }
      }
    }

    this.state = {
      isUpdating: !!url,
      isUploading: false,
      url,
      height,
      width,
      alt,
      transcriptLink,
      imageLink,
      file: null,
      percent: 0,
      message: ''
    };
  }

  updateProgress = (event) => {
    const { isUploading } = this.state;

    if (event.lengthComputable && event.loaded && event.total) {
      if (isUploading === false) {
        this.setState({ isUploading: true });
      }
      const percent = Math.ceil((event.loaded / event.total) * 100);
      if (percent < 100) {
        this.setState({ percent });
      }
    }
  };

  handleHeight = (height) => {
    this.setState({ height });
  };

  handleWidth = (width) => {
    this.setState({ width });
  };

  handleAlt = (alt) => {
    this.setState({ alt });
  };

  handleTranscriptLink = (transcriptLink) => {
    this.setState({ transcriptLink });
  };

  handleImageHyperlink = (imageLink) => {
    this.setState({ imageLink });
  };

  handleDrop = (files) => {
    const file = files[0];
    this.setState({ file });
    this.handleMediaUpload(file);
  };

  handleDropRejected = (fileRejections: FileRejection[]) => {
    const { messages } = this.props;
    const fileRejection = fileRejections[0];
    const file = fileRejection?.file;
    if (file?.type.includes('image') && file?.size > getFileSizeInBytes(MAX_IMAGE_UPLOAD_SIZE)) {
      viewActionCreators.showCapitalToast({
        title: messages.failedToUpload,
        body: messages.failedToUploadImage,
        variant: 'failure'
      });
    } else if (file?.type.includes('video') && file?.size > getFileSizeInBytes(MAX_VIDEO_UPLOAD_SIZE)) {
      viewActionCreators.showCapitalToast({
        title: messages.failedToUpload,
        body: messages.failedToUploadVideo,
        variant: 'failure'
      });
    }
  };

  handleReset = () => {
    this.setState({
      isUpdating: true,
      isUploading: false,
      url: '',
      alt: '',
      transcriptLink: '',
      height: null,
      width: null,
      file: null,
      percent: 0,
      message: ''
    });
  };

  handleMediaUpload = (media) => {
    const { entitySubdomain, messages } = this.props;

    if (media) {
      const uploadFile = (file, signedRequest, url) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = this.updateProgress;
        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            if (xhr.status === 200) {
              this.setState({ isUploading: false, url });
            } else {
              this.setState({ isUploading: false, message: messages.errorUploadingMedia });
            }
          }
        };
        xhr.open('PUT', signedRequest);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      };

      // fetch (not XHR): in PSP mode auth (bearer + credentials) is attached
      // by the window.fetch wrapper in psp/pspEntry.tsx — raw XHR bypasses it
      // and the sign request 401s. The S3 PUT above stays on XHR for upload
      // progress events; it must not carry our auth headers anyway.
      const getSignedRequest = (file) => {
        fetch(getS3SignUrl({ fileName: file.name, fileType: file.type, entitySubdomain }))
          .then((res) => {
            if (!res.ok) throw new Error(`sign-s3 failed: ${res.status}`);
            return res.json();
          })
          .then((response) => {
            uploadFile(file, response.signedRequest, response.url);
          })
          .catch(() => {
            this.setState({ isUploading: false, message: messages.couldNotGetURL });
          });
      };

      getSignedRequest(media);
    }
  };

  handleRemove = () => {
    const {
      mediaType = MEDIA_IMAGE,
      storyContent,
      story,
      storyContent: { mediaIndex = 0 },
      onClose,
      updateStoryContent,
      updateStoryContentDB,
      isTemplate
    } = this.props;

    if (mediaType === MEDIA_BACKGROUND) {
      updateStoryContent(removeBackground(storyContent));
      updateStoryContentDB({ storyContent: removeBackground(storyContent), isTemplate: isTemplate, story: story });
    } else {
      updateStoryContent(removeMedia(storyContent, mediaIndex));
      updateStoryContentDB({
        storyContent: removeMedia(storyContent, mediaIndex),
        isTemplate: isTemplate,
        story: story
      });
    }

    onClose();
  };

  handleSubmit = (e, rawUrl) => {
    e.preventDefault();
    const url = sanitizeUrl(rawUrl);
    if (!url) return;
    const { isUpdating, height, width, alt, transcriptLink, imageLink } = this.state;
    const {
      mediaType = MEDIA_IMAGE,
      storyContent,
      storyContent: { mediaIndex = 0 },
      messages,
      onClose,
      updateStoryContent,
      updateStoryContentDB,
      isTemplate,
      story
    } = this.props;
    const pattern = /^((http|https):\/\/)/;
    let metaStoryContent = storyContent;
    let imageHref = imageLink;
    let newStoryContent;
    let linkMedia;
    let media = `<img ${height ? ` height=${height}` : ''} ${width ? ` width=${width}` : ''} ${` alt="${alt ? alt : ''
      }"`} ${imageLink ? ` data-image-link="${imageLink}"` : ''} src="${url}" />`;

    if (imageHref) {
      if (!pattern.test(imageHref)) {
        imageHref = `http://${imageHref}`;
      }
      if (mediaType === MEDIA_IMAGE) {
        media = `<a href=${imageHref} target="_blank" rel="noopener noreferrer" data-is-block="true"><img ${height ? ` height=${height}` : ''
          } ${width ? ` width=${width}` : ''} ${alt ? ` alt="${alt}"` : ''} src="${url}" /></a>`;
      }
    }

    if (mediaType === MEDIA_VIDEO) {
      media = `<video ${height ? ` height=${height}` : ''} ${width ? ` width=${width}` : ''} ${alt ? ` alt="${alt}"` : ''
        } controls src="${url}" />`;
      if (transcriptLink) {
        linkMedia = `<div class="transcript-link"><a href="${transcriptLink}">${messages.videoTranscript}</a></div>`;
      }
    }

    if (isUpdating) {
      if (mediaType === MEDIA_BACKGROUND) {
        metaStoryContent = removeBackground(storyContent);
      } else {
        metaStoryContent = removeMedia(storyContent, mediaIndex);
      }
    }

    if (mediaType === MEDIA_BACKGROUND) {
      newStoryContent = addBackground(metaStoryContent, url);
    } else {
      newStoryContent = addMedia(metaStoryContent, media, mediaIndex, mediaType);
      if (mediaType === MEDIA_VIDEO && transcriptLink && linkMedia) {
        newStoryContent = addMedia(newStoryContent, linkMedia, mediaIndex, mediaType);
      }
    }

    onClose();
    updateStoryContent(newStoryContent);
    updateStoryContentDB({ storyContent: newStoryContent, isTemplate: isTemplate, story: story });
  };

  renderContent = () => {
    const { mediaType = MEDIA_IMAGE, messages } = this.props;
    const { isUploading, url, height, width, alt, transcriptLink, percent, message, imageLink } = this.state;
    let mediaTag = <img alt={alt || 'Image preview'} src={sanitizeUrl(url)} data-test="media-image" />;

    if (mediaType === MEDIA_VIDEO) {
      mediaTag = <video controls={true} src={sanitizeUrl(url)} aria-label={alt || 'Video preview'} data-test="media-video" />;
    }

    if (message) {
      return (
        <div className={styles.centered}>
          <p style={{ padding: '2rem 0' }}>{message}</p>
        </div>
      );
    } else if (isUploading && percent < 100) {
      return (
        <div className={styles.centered} data-test="media-uploading">
          <p style={{ padding: '2rem 0' }}>
            {messages.uploading}... {percent}%
          </p>
        </div>
      );
    } else if (url) {
      return (
        <div>
          <div className={styles.preview}>
            <button type="button" className={styles.icon} onClick={this.handleReset} aria-label="Remove media">
              <span className="icon-remove-2" />
            </button>
            {mediaTag}
          </div>
          <Box sx={muiStyles.form}>
            {mediaType === MEDIA_VIDEO && (
              <Box sx={muiStyles.fieldGroup}>
                <Typography variant="body2">{messages.transcriptLink}</Typography>
                <TextField
                  size="small"
                  fullWidth
                  name={messages.transcriptLink}
                  placeholder={messages.transcriptLinkPlaceholder}
                  value={transcriptLink}
                  onChange={(e) => this.handleTranscriptLink(e.target.value)}
                  inputProps={{ 'data-test': 'media-video-transcript', className: 'no-format' }}
                />
              </Box>
            )}
            {mediaType !== MEDIA_BACKGROUND && (
              <Box sx={muiStyles.fieldGroup}>
                <Typography variant="body2">{messages.alt}</Typography>
                <TextField
                  size="small"
                  fullWidth
                  name={messages.alt}
                  placeholder={messages.altPlaceholder}
                  value={alt}
                  onChange={(e) => this.handleAlt(e.target.value)}
                  inputProps={{ 'data-test': 'media-alt-text', className: 'no-format' }}
                />
              </Box>
            )}
            {mediaType === MEDIA_IMAGE && (
              <Box sx={muiStyles.fieldGroup}>
                <Box sx={muiStyles.row}>
                  <Box sx={muiStyles.halfCol}>
                    <Typography variant="body2">
                      {messages.height} <Typography component="span" variant="caption">{messages.optional}</Typography>
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      name={messages.height}
                      placeholder="auto"
                      value={height || ''}
                      inputProps={{ min: 0, max: 2000, 'data-test': 'media-image-height', className: 'no-format' }}
                      onChange={(e) => this.handleHeight(e.target.value)}
                    />
                  </Box>
                  <Box sx={muiStyles.halfCol}>
                    <Typography variant="body2">
                      {messages.width} <Typography component="span" variant="caption">{messages.optional}</Typography>
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      name={messages.width}
                      placeholder="auto"
                      value={width || ''}
                      inputProps={{ min: 0, max: 2000, 'data-test': 'media-image-width', className: 'no-format' }}
                      onChange={(e) => this.handleWidth(e.target.value)}
                    />
                  </Box>
                </Box>
                <Box sx={muiStyles.fieldGroup}>
                  <Typography variant="body2">{messages.imageLink}</Typography>
                  <TextField
                    size="small"
                    fullWidth
                    name={messages.imageLink}
                    placeholder={messages.imageLinkPlaceholder}
                    value={imageLink}
                    onChange={(e) => this.handleImageHyperlink(e.target.value)}
                    inputProps={{ 'data-test': 'media-image-link', className: 'no-format' }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </div>
      );
    }

    return (
      <Dropzone
        multiple={false}
        accept={mediaType === MEDIA_VIDEO ? ACCEPT_VIDEO : ACCEPT_IMAGE}
        maxSize={
          mediaType === MEDIA_VIDEO
            ? getFileSizeInBytes(MAX_VIDEO_UPLOAD_SIZE)
            : getFileSizeInBytes(MAX_IMAGE_UPLOAD_SIZE)
        }
        onDrop={this.handleDrop}
        onDropRejected={this.handleDropRejected}
      >
        {({ getRootProps, getInputProps, isDragActive, isDragReject }) => {
          const dropzoneStyle = isDragReject
            ? muiStyles.dropzoneReject
            : isDragActive
              ? muiStyles.dropzoneActive
              : muiStyles.dropzoneDefault;

          return (
            <Box {...getRootProps()} sx={dropzoneStyle}>
              <input {...getInputProps()} aria-label={`Upload ${mediaType}`} />
              <div className={styles.upload}>
                <p>
                  {messages.dragThe}&nbsp;
                  {mediaType === MEDIA_VIDEO ? 'MP4 ' : ''}
                  {mediaType}&nbsp;
                  {messages.intoThisBox}
                  <br />
                  {messages.orClickToSelect}
                </p>
                <Box sx={muiStyles.uploadContainer}>
                  <Button variant="contained" color="primary" size="small" data-test="media-upload">
                    {messages.upload}
                  </Button>
                </Box>
              </div>
            </Box>
          );
        }}
      </Dropzone>
    );
  };

  renderSubmit = () => {
    const { isUpdating, url } = this.state;
    const { messages } = this.props;
    const submitText = isUpdating ? messages.update : messages.add;

    return (
      <Button
        variant="contained"
        color="primary"
        size="small"
        disabled={!url}
        onClick={(e) => url && this.handleSubmit(e, url)}
        data-test={url ? 'media-save-attributes' : 'media-save-upload'}
      >
        {submitText}
      </Button>
    );
  };

  render() {
    const { isUpdating, isUploading, url } = this.state;
    const { mediaType = MEDIA_IMAGE, messages, onClose } = this.props;
    let title;

    if (mediaType === MEDIA_VIDEO) {
      title = isUpdating ? messages.updateVideo : messages.addVideo;
    } else if (mediaType === MEDIA_BACKGROUND) {
      title = isUpdating ? messages.updateBackground : messages.addBackground;
    } else {
      title = isUpdating ? messages.updateImage : messages.addImage;
    }

    const fileSize = mediaType === MEDIA_VIDEO ? MAX_VIDEO_UPLOAD_SIZE : MAX_IMAGE_UPLOAD_SIZE;

    return (
      <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="add-media-dialog-title">
        <DialogTitle id="add-media-dialog-title" sx={muiStyles.dialogTitle}>
          {title}
          <IconButton aria-label="close" onClick={this.props.onClose} size="small" sx={muiStyles.closeButton}>
            <SvgIcon component={CloseIcon} inheritViewBox />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={muiStyles.dialogContent}>
          {this.renderContent()}
          {!isUploading && !url && (
            <Typography variant="body2" sx={muiStyles.fileSize}>
              {`${messages.maximumFileSize} ${fileSize} MB`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={muiStyles.dialogActions}>
          <Button variant="outlined" color="secondary" size="small" onClick={this.props.onClose} data-test="media-cancel-upload">
            {messages.cancel}
          </Button>
          {isUpdating && (
            <Button variant="outlined" color="error" size="small" onClick={this.handleRemove}>
              {messages.remove}
            </Button>
          )}
          {this.renderSubmit()}
        </DialogActions>
      </Dialog>
    );
  }
}
